import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";

const PhoneTypeList: { [key: string]: string } = {
  MOBILE: "Mobile",
  HOME: "Home",
  WORK: "Work",
  FAX: "Fax",
  OTHER: "Other",
};

interface User {
  id: string;
  account?: string;
  stripeSessionId?: string;
  clinikoApiKey?: string;
  cancelled?: string;
  currentPeriodEnd?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  initials?: string;
  photoUrl?: string;
  dob?: Date;
  sex?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postcode?: string;
  email?: string;
  phone?: string;
  phoneType?: string;
  occupation?: string;
  clinicName?: string;
  clinicRole?: string;
  clinicAddress1?: string;
  clinicAddress2?: string;
  clinicAddress3?: string;
  clinicPostCode?: string;
  clinicEmail?: string;
  clinicPhone?: string;
  clinicPhoneType?: string;
}

const AccountClinicModal = ({
  db,
  currentUser,
}: {
  db: any;
  currentUser: User;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [clinicRole, setClinicRole] = useState(currentUser.clinicRole || "");
  const [clinicName, setClinicName] = useState(currentUser.clinicName || "");
  const [clinicAddress1, setClinicAddress1] = useState(
    currentUser.clinicAddress1 || ""
  );
  const [clinicAddress2, setClinicAddress2] = useState(
    currentUser.clinicAddress2 || ""
  );
  const [clinicAddress3, setClinicAddress3] = useState(
    currentUser.clinicAddress3 || ""
  );
  const [clinicPostCode, setClinicPostCode] = useState(
    currentUser.clinicPostCode || ""
  );
  const [clinicEmail, setClinicEmail] = useState(currentUser.clinicEmail || "");
  const [clinicPhone, setClinicPhone] = useState(currentUser.clinicPhone || "");
  const [clinicPhoneType, setClinicPhoneType] = useState(
    currentUser.clinicPhoneType || "MOBILE"
  );

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    const userDocRef = doc(db, "users", currentUser.id);
    try {
      await updateDoc(userDocRef, {
        clinicRole: clinicRole,
        clinicName: clinicName,
        clinicAddress1: clinicAddress1,
        clinicAddress2: clinicAddress2,
        clinicAddress3: clinicAddress3,
        clinicPostCode: clinicPostCode,
        clinicEmail: clinicEmail,
        clinicPhone: clinicPhone,
        clinicPhoneType: clinicPhoneType,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating clinic information:", error);
    }
  };

  const inputStyle: React.CSSProperties = {
    boxSizing: "border-box",
    margin: "0 0 1rem 0",
    padding: "0.5rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    width: "100%",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginBottom: "1rem",
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {isEditing ? (
          <button className="button-copy" onClick={handleSaveClick}>
            Save
          </button>
        ) : (
          <button className="button-copy" onClick={handleEditClick}>
            Edit
          </button>
        )}
      </div>
      <div style={{ marginBottom: "0.5rem" }}>
        <label
          style={{
            display: "block",
            marginBottom: "0.5rem",
            fontWeight: "bold",
          }}
        >
          Clinic Role
        </label>
        {isEditing ? (
          <input
            style={inputStyle}
            type="text"
            value={clinicRole}
            onChange={(e) => setClinicRole(e.target.value)}
            placeholder="Clinic Role (e.g., Senior Physiotherapist)"
          />
        ) : (
          <div style={{ marginBottom: "1.5rem" }}>
            {clinicRole || "Not Provided"}
          </div>
        )}
      </div>

      <div style={{ marginBottom: "0.5rem" }}>
        <label
          style={{
            display: "block",
            marginBottom: "0.5rem",
            fontWeight: "bold",
          }}
        >
          Clinic Name
        </label>
        {isEditing ? (
          <input
            style={inputStyle}
            type="text"
            value={clinicName}
            onChange={(e) => setClinicName(e.target.value)}
            placeholder="Clinic Name (e.g., ABC Clinic)"
          />
        ) : (
          <div style={{ marginBottom: "1.5rem" }}>
            {clinicName || "Not Provided"}
          </div>
        )}
      </div>

      <div style={{ marginBottom: "0.5rem" }}>
        <label
          style={{
            display: "block",
            marginBottom: "0.5rem",
            fontWeight: "bold",
          }}
        >
          Clinic Address
        </label>
        {isEditing ? (
          <>
            <input
              style={{ ...inputStyle, margin: "0 0 0.2rem 0" }}
              type="text"
              value={clinicAddress1}
              onChange={(e) => setClinicAddress1(e.target.value)}
              placeholder="Address Line 1"
            />
            <input
              style={{ ...inputStyle, margin: "0 0 0.2rem 0" }}
              type="text"
              value={clinicAddress2}
              onChange={(e) => setClinicAddress2(e.target.value)}
              placeholder="Address Line 2"
            />
            <input
              style={{ ...inputStyle, margin: "0 0 0.2rem 0" }}
              type="text"
              value={clinicAddress3}
              onChange={(e) => setClinicAddress3(e.target.value)}
              placeholder="Address Line 3"
            />
            <input
              style={inputStyle}
              type="text"
              value={clinicPostCode}
              onChange={(e) => setClinicPostCode(e.target.value)}
              placeholder="Post Code"
            />
          </>
        ) : (
          <div style={{ marginBottom: "1.5rem" }}>
            <div>{clinicAddress1 || "Not Provided"}</div>
            {clinicAddress2 && <div>{clinicAddress2}</div>}
            {clinicAddress3 && <div>{clinicAddress3}</div>}
            {clinicPostCode && <div>{clinicPostCode}</div>}
          </div>
        )}
      </div>

      <div style={{ marginBottom: "0.5rem" }}>
        <label
          style={{
            display: "block",
            marginBottom: "0.5rem",
            fontWeight: "bold",
          }}
        >
          Clinic Email
        </label>
        {isEditing ? (
          <input
            style={inputStyle}
            type="email"
            value={clinicEmail}
            onChange={(e) => setClinicEmail(e.target.value)}
            placeholder="Clinic Email (e.g., hello@londonclinic.com)"
          />
        ) : (
          <div style={{ marginBottom: "1.5rem" }}>
            {clinicEmail || "Not Provided"}
          </div>
        )}
      </div>

      <div style={{ marginBottom: "0.5rem", display: "flex", gap: "1rem" }}>
        <div style={{ flexGrow: 1 }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
            }}
          >
            Clinic Phone
          </label>
          {isEditing ? (
            <input
              style={inputStyle}
              type="tel"
              value={clinicPhone}
              onChange={(e) => setClinicPhone(e.target.value)}
              placeholder="+442111222333"
            />
          ) : (
            <div style={{ marginBottom: "1.5rem" }}>
              {clinicPhone || "Not Provided"}
            </div>
          )}
        </div>
        <div style={{ flexGrow: 1 }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
            }}
          >
            Clinic Phone Type
          </label>
          {isEditing ? (
            <select
              style={inputStyle}
              value={clinicPhoneType}
              onChange={(e) => setClinicPhoneType(e.target.value)}
            >
              {Object.entries(PhoneTypeList).map(([value, label])  => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ marginBottom: "1.5rem" }}>
              {PhoneTypeList[clinicPhoneType] ||
                "Not Provided"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountClinicModal;
